import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { useGetGamblingSessionLimits } from 'server';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { showCommonPopupAction } from 'js/components/common/popups/CommonPopup';
import { showSimpleDialogInfoPopupAction } from 'js/components/popups/SimpleDialogInfoPopup';
import { showGamblingSessionLimitsPopupAction } from 'js/components/popups/GamblingSessionLimitsPopup';

import * as Types from '../types';
import { useOpenGame } from './useOpenGame';
import { GAME_LAUNCH_METHODS } from '../constants';
import { useGetLaunchMethod } from './useGetLaunchMethod';

export const useLaunchWithLimitsVerification = () => {
    const dispatch = useDispatch();
    const getGamblingSessionLimits = useGetGamblingSessionLimits();
    const getLaunchMethod = useGetLaunchMethod();
    const openGame = useOpenGame();
    const enableGamblingSessionLimitPopup = useAppSelector(prop('enableGamblingSessionLimitPopup'));
    const getErrorMessage = useGetErrorMessage();

    return useCallback(async (launchGameData: Types.ILaunchGameData): Promise<void> => {
        const { gameCode, mode } = launchGameData;
        const launchMethod = getLaunchMethod({ gameCode });
        const isVerificationNeeded = mode === 1
            && enableGamblingSessionLimitPopup
            && launchMethod
            && [GAME_LAUNCH_METHODS.REDIRECT_IN_NEW_WINDOW, GAME_LAUNCH_METHODS.REDIRECT_IN_THIS_WINDOW].includes(launchMethod);
        const showGamblingSessionLimitsPopup = () => dispatch(showGamblingSessionLimitsPopupAction({
            onSuccessLimitsSet: () => openGame(launchGameData)
        }));

        if (isVerificationNeeded) {
            try {
                const { gamblingSessionId, actions } = await getGamblingSessionLimits();
                const action = actions?.find(item => item.actionShowSimpleDialogInfo)?.actionShowSimpleDialogInfo;

                if (action) {
                    dispatch(showSimpleDialogInfoPopupAction({
                        message: action.message,
                        actionId: action.actionId,
                        onAccept: showGamblingSessionLimitsPopup,
                        acceptBtnText: 'playtech.mobile.system.gamblingSessionLimits.intervalPopup.submit',
                        declineBtnText: 'playtech.mobile.system.gamblingSessionLimits.intervalPopup.cancel'
                    }));

                    return;
                }

                if (!gamblingSessionId) {
                    showGamblingSessionLimitsPopup();

                    return;
                }
            } catch (error: any) {
                dispatch(showCommonPopupAction({
                    title: 'playtech.mobile.system.gamblingSessionLimits.errorPopup.title',
                    content: getErrorMessage(error, error?.response?.data?.errorMessage)
                }));

                return;
            }
        }

        openGame(launchGameData);
    }, [openGame, dispatch, enableGamblingSessionLimitPopup, getLaunchMethod, getErrorMessage, getGamblingSessionLimits]);
};
